import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useToast } from './Toast';

const API_BASE_URL = process.env.REACT_APP_API_URL + '/streaming';

export default function AddAlbumModal({ isOpen, onClose, group = 'Unsigned', queryKeyToInvalidate = ['unsigned-albums'] }) {
  const [addMode, setAddMode] = useState(null); // null, 'id', or 'search'
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedAlbums, setSelectedAlbums] = useState([]);
  const [albumIdInput, setAlbumIdInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  // '' = default (server inherits the artist's existing label, or Tracking).
  const [addLabel, setAddLabel] = useState('');

  const queryClient = useQueryClient();
  const toast = useToast();

  // Priority options for the add flow (default + labels, minus None/Destroy).
  const addPriorityOptions = [
    { value: '', label: 'Default (keep existing / Tracking)' },
    { value: 'Tracking', label: 'Tracking' },
    { value: 'Archive', label: 'Archive' },
    { value: 'Agenda - Legal', label: 'Agenda - Legal' },
    { value: 'Agenda - Priorities', label: 'Agenda - Priorities' },
    { value: 'Agenda - New', label: 'Agenda - New' },
    { value: 'Agenda - Tracking', label: 'Agenda - Tracking' },
  ];

  // The Priority (tracking label) picker only applies to the Unsigned add flow.
  // Other rosters (e.g. group="Encore") have no tracking label, so hide it.
  const renderPrioritySelect = () => {
    if (group !== 'Unsigned') return null;
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
        <select
          value={addLabel}
          onChange={(e) => setAddLabel(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
        >
          {addPriorityOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  };

  const resetModal = () => {
    setAddMode(null);
    setStep(1);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedAlbums([]);
    setAlbumIdInput('');
    setIsSearching(false);
    setIsAdding(false);
    setAddLabel('');
    onClose();
  };

  // Determine the correct endpoint based on group
  const getAddEndpoint = () => {
    if (group === 'Unsigned') {
      return `${API_BASE_URL}/add-unsigned-album`;
    }
    return `${API_BASE_URL}/add-album`;
  };

  const addAlbumMutation = useMutation(
    async ({ albumId, trackingLabel }) => {
      const endpoint = getAddEndpoint();
      const payload = group === 'Unsigned'
        ? { luminate_id: albumId, tracking_label: trackingLabel || undefined }
        : { luminate_id: albumId, group, tracking_label: trackingLabel || undefined };
      const response = await axios.post(endpoint, payload);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeyToInvalidate);
        toast.success('Album added successfully', `Album added to ${group} catalog`);
      },
      onError: (error) => {
        console.error('Error adding album:', error);
        const errorMessage = error.response?.data?.error || 'Error adding album. Please try again.';
        toast.error('Failed to add album', errorMessage);
      }
    }
  );

  const handleSearchAlbums = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/search-albums`, {
        query: searchQuery
      });
      setSearchResults(response.data.results || []);
      setStep(2);
    } catch (error) {
      console.error('Error searching albums:', error);
      toast.error('Search failed', 'Error searching for albums');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddAlbum = async () => {
    // Capture the chosen label before resetModal() clears it.
    const trackingLabel = addLabel;
    if (addMode === 'id') {
      if (!albumIdInput.trim()) return;
      const albumId = albumIdInput.trim();
      resetModal();
      const toastId = toast.loading('Adding album...', 'Processing in background');
      addAlbumMutation.mutate({ albumId, trackingLabel }, {
        onSettled: () => toast.dismiss(toastId)
      });
    } else {
      if (selectedAlbums.length === 0) return;
      const albumsToAdd = [...selectedAlbums];
      resetModal();
      const toastId = toast.loading('Adding albums...', `Processing ${albumsToAdd.length} album(s) in background`);
      let completed = 0;
      albumsToAdd.forEach(albumId => {
        addAlbumMutation.mutate({ albumId, trackingLabel }, {
          onSettled: () => {
            completed++;
            if (completed === albumsToAdd.length) {
              toast.dismiss(toastId);
            }
          }
        });
      });
    }
  };

  const toggleAlbumSelection = (albumId) => {
    setSelectedAlbums(prev => {
      if (prev.includes(albumId)) {
        return prev.filter(id => id !== albumId);
      }
      return [...prev, albumId];
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {!addMode ? 'Add Album' : addMode === 'id' ? 'Add Album by ID' : step === 1 ? 'Search for an Album' : 'Select Albums'}
            </h3>
            <button
              onClick={resetModal}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Mode Selection */}
          {!addMode && (
            <div className="space-y-3">
              <button
                onClick={() => setAddMode('id')}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Add by Luminate ID
              </button>
              <button
                onClick={() => setAddMode('search')}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search for Album
              </button>
            </div>
          )}

          {/* Add by ID */}
          {addMode === 'id' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Luminate Album ID
                </label>
                <input
                  type="text"
                  value={albumIdInput}
                  onChange={(e) => setAlbumIdInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddAlbum()}
                  placeholder="Paste Luminate Album ID..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isAdding}
                />
              </div>
              {renderPrioritySelect()}
              <div className="flex gap-2">
                <button
                  onClick={() => setAddMode(null)}
                  disabled={isAdding}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-200 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={handleAddAlbum}
                  disabled={isAdding || !albumIdInput.trim()}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isAdding ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Adding...
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Album
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Search */}
          {addMode === 'search' && step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Album Title
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchAlbums()}
                  placeholder="Enter album title to search..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSearching}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setAddMode(null)}
                  disabled={isSearching}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-200 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={handleSearchAlbums}
                  disabled={isSearching || !searchQuery.trim()}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSearching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Searching...
                    </>
                  ) : (
                    'Search'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Select Albums */}
          {addMode === 'search' && step === 2 && (
            <div className="space-y-4">
              <div className="max-h-60 overflow-y-auto">
                {searchResults.map((album) => (
                  <div
                    key={album.id}
                    onClick={() => toggleAlbumSelection(album.id)}
                    className={`p-3 border rounded-md mb-2 cursor-pointer hover:bg-gray-50 ${
                      selectedAlbums.includes(album.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedAlbums.includes(album.id)}
                        onChange={() => toggleAlbumSelection(album.id)}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{album.display_artist_name} - {album.title}</div>
                        {album.upc && (
                          <div className="text-xs text-gray-400">
                            UPC: {album.upc}
                          </div>
                        )}
                        {album.album_type && (
                          <div className="text-xs text-gray-400">
                            Type: {album.album_type}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {renderPrioritySelect()}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setStep(1);
                    setSelectedAlbums([]);
                  }}
                  disabled={isAdding}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:bg-gray-200 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={handleAddAlbum}
                  disabled={isAdding || selectedAlbums.length === 0}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isAdding ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Adding...
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add {selectedAlbums.length} Album{selectedAlbums.length !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
